const getClearedErrorObj = errorObj =>
  Object.fromEntries(Object.keys(errorObj).map(key => [key, null]));

const getNextID = ({ prefix, lastID }) => {
  const idInteger = lastID ? Number(lastID.split('-')[1]) + 1 : 1;
  return `${prefix}-${idInteger.toString().padStart(4, '0')}`;
};

const organizeUserAddress = address => {
  if (typeof address !== 'string') return;

  const addressArr = Array.from(address.replace(/={3}/g, ', '));
  addressArr.splice(addressArr.lastIndexOf(','), 1, '.');
  return addressArr.join('');
};

const toCapitalize = words => {
  if (words)
    return words
      .split('_')
      .map(word => word[0].toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
};

const currencyFormatter = Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'LKR',
});

function toCurrencyString(value) {
  if (typeof value === 'number') {
    const formattedValue = currencyFormatter.format(value);
    return formattedValue;
  }
  throw new TypeError(`expected number, received ${typeof value}`);
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
async function fileToBase64String(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

function base64ToFile(base64) {
  const { data, mimetype, extension } = extractBase64Parts(base64);
  const sliceSize = 1024;
  const byteCharacters = atob(data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);
    const bytes = new Array(end - begin);

    for (let offset = begin, i = 0; offset < end; ++i, ++offset)
      bytes[i] = byteCharacters[offset].charCodeAt(0);

    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }

  const blob = new Blob(byteArrays, { type: mimetype });
  const filename = `${Math.random()}_${Date.now()}.${extension}`;
  return new File([blob], filename, { type: mimetype });
}

/**
 * @param {string} base64
 * @returns {{extension: string, mimetype: string,data: string}}
 */
function extractBase64Parts(base64) {
  const parts = base64.split(';');
  const extension = parts[0].split('/')[1];
  const mimetype = parts[0].split(':')[1];
  const data = parts[1].split(',').pop();
  return { extension, mimetype, data };
}

/**
 * True for a freshly-picked image still held as a base64 preview (`data:` URI),
 * as opposed to a persisted S3 URL. Bucket/region-independent on purpose — the
 * frontend no longer knows the bucket (uploads go through the BFF presigned flow).
 * @param {unknown} value
 */
function isDataUrl(value) {
  return typeof value === 'string' && value.startsWith('data:');
}

/**
 * Render a stored phone number for display. Numbers are persisted canonically as
 * E.164 (`+94771900136`); users expect the local form (`0771900136`). Strips any
 * `+94`/`94` country prefix and shows a leading `0`. Leaves unrecognised / short
 * values untouched.
 * @param {unknown} value
 */
function toLocalPhone(value) {
  if (typeof value !== 'string') return value ?? '';
  const digits = value.replace(/\D/g, '');
  if (digits.length < 9) return value;
  return `0${digits.slice(-9)}`;
}

export {
  getClearedErrorObj,
  getNextID,
  organizeUserAddress,
  toCapitalize,
  toCurrencyString,
  fileToBase64String,
  base64ToFile,
  isDataUrl,
  toLocalPhone,
};
